<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink } from 'vue-router';

import { useError } from '../../composables/useError';

const { error } = useError();

/**
 * Maps internal error state to Nuxt UI UError component props.
 * - error.title -> error.statusMessage (displayed as main heading)
 * - error.description -> error.message (displayed as subtext)
 */
const errorProps = computed(() => {
  if (!error.value) {
    return null;
  }

  return {
    statusCode: error.value.statusCode,
    statusMessage: error.value.title,
    message: error.value.description,
  };
});

const isCritical = computed(() => error.value?.critical === true);

const handleReload = () => {
  window.location.reload();
};
</script>

<template>
  <!-- `flex-1` is required for the critical-error case: there the view is a direct
       child of UDashboardGroup (`fixed inset-0 flex`), so without it the root
       shrinks to its content width and sits against the left edge instead of
       centering. `min-h-svh` replaces the theme's `100vh - header` default —
       nothing renders above the error view here, so it owns the full viewport. -->
  <UError
    v-if="errorProps"
    :error="errorProps"
    :clear="false"
    :ui="{ root: 'flex-1 min-h-svh px-4' }"
    data-testid="u-error"
  >
    <template #links>
      <!-- Critical error: reload page -->
      <UButton
        v-if="isCritical"
        size="lg"
        color="primary"
        label="Reload page"
        @click="handleReload"
      />
      <!-- Non-critical error: back to home -->
      <!-- Error is cleared by beforeEach error middleware when navigation starts -->
      <UButton
        v-else
        :as="RouterLink"
        to="/"
        size="lg"
        color="primary"
        label="Go home"
      />
    </template>
  </UError>
</template>
